<!-- PatientActionButton -->
<script setup lang="ts">
    import '../../../../css/choice.css';
    import { reactive, watch, computed, onMounted } from 'vue';
    import { IPatientBalanceItem, PatientBalanceType } from '../../../lib/Models/PatientModel';
    import * as ChoiceApi from '../../../lib/ChoiceApi';
    import { DatabaseIdTypes } from '../../../Enums/ChoiceEnums';
    import Currency from '../../../components/Formatting/Currency.vue';

    const props = defineProps<{
        databaseIdType: DatabaseIdTypes,
        identifier?: number,
        refreshNow?: boolean
    }>();

    const formData = reactive<{
        balance?: IPatientBalanceItem[]
    }>({
    });

    onMounted(async () => {
        if (props.refreshNow)
            refresh();
    });
    defineExpose({refresh});

    const emit = defineEmits<{
        (e: "showBalance", strValue: number): void
      }>();

    watch(() => props.databaseIdType, (newVal, oldVal) => {
        refresh();
    });
    watch(() => props.identifier, (newVal, oldVal) => {
        refresh();
    });

    async function refresh()
    {
        formData.balance = undefined;
        if (props.databaseIdType !== DatabaseIdTypes.patient_id && props.databaseIdType !== DatabaseIdTypes.patient_entity_id)
            return;
        if (!props.identifier)
            return;
        formData.balance = await ChoiceApi.patient.getPatientBalance(props.databaseIdType, props.identifier);
        if (!formData.balance)
            return;
        if (!patientBalance.value)
            return; 
        emit("showBalance", patientBalance.value.totalBalance);
    }

    const patientBalance = computed<IPatientBalanceItem | undefined>(() => formData.balance?.find(item => item.balanceType === PatientBalanceType.Patient));

    function getPatientBalanceProperty(propertyName: string): any {
        return patientBalance.value ? (patientBalance.value as any)[propertyName] : undefined;
    }

    const insuranceBalance = computed<IPatientBalanceItem | undefined>(() => formData.balance?.find(item => item.balanceType === PatientBalanceType.Insurance));

    function getInsuranceBalanceProperty(propertyName: string): any {
        return insuranceBalance.value ? (insuranceBalance.value as any)[propertyName] : undefined;
    }

    const unsubmittedBalance = computed<IPatientBalanceItem | undefined>(() => formData.balance?.find(item => item.balanceType === PatientBalanceType.Unsubmitted));

    function getUnsubmittedBalanceProperty(propertyName: string): any {
        return unsubmittedBalance.value ? (unsubmittedBalance.value as any)[propertyName] : undefined;
    }



</script>

<style scoped>

</style>

<template>
    <template v-if="patientBalance"> 
        <div class="row mt-2">
            <div class="col table-header-background label-isalus-small">
                Patient Balance: <Currency :value="patientBalance.totalBalance"/>
            </div>
        </div>
        <div class="row">
            <template v-for="i in 8" :key="i">
                <div v-if="getPatientBalanceProperty(`label${i}`)" class="col label-isalus-small isalus-label-secondary text-end">
                    {{ getPatientBalanceProperty(`label${i}`) }}
                </div>
            </template>
        </div>
        <div class="row">
            <template v-for="i in 8" :key="i">
                <div v-if="getPatientBalanceProperty(`label${i}`)" class="col label-isalus-small text-end">
                    <Currency :value="getPatientBalanceProperty(`balance${i}`)"/>
                </div>
            </template>
        </div>
    </template>
    <template v-if="insuranceBalance"> 
        <div class="row mt-2">
            <div class="col table-header-background label-isalus-small">
                Insurance Balance: <Currency :value="insuranceBalance.totalBalance"/>
            </div>
        </div>
        <div class="row">
            <template v-for="i in 8" :key="i">
                <div v-if="getInsuranceBalanceProperty(`label${i}`)" class="col label-isalus-small isalus-label-secondary text-end">
                    {{ getInsuranceBalanceProperty(`label${i}`) }}
                </div>
            </template>
        </div>
        <div class="row">
            <template v-for="i in 8" :key="i">
                <div v-if="getInsuranceBalanceProperty(`label${i}`)" class="col label-isalus-small text-end">
                    <Currency :value="getInsuranceBalanceProperty(`balance${i}`)"/>
                </div>
            </template>
        </div>
    </template>
    <template v-if="unsubmittedBalance"> 
        <div class="row mt-2">
            <div class="col table-header-background label-isalus-small">
                Unsubmitted Balance: <Currency :value="unsubmittedBalance.totalBalance"/>
            </div>
        </div>
        <div class="row">
            <template v-for="i in 8" :key="i">
                <div v-if="getUnsubmittedBalanceProperty(`label${i}`)" class="col label-isalus-small isalus-label-secondary text-end">
                    {{ getUnsubmittedBalanceProperty(`label${i}`) }}
                </div>
            </template>
        </div>
        <div class="row">
            <template v-for="i in 8" :key="i">
                <div v-if="getUnsubmittedBalanceProperty(`label${i}`)" class="col label-isalus-small text-end">
                    <Currency :value="getUnsubmittedBalanceProperty(`balance${i}`)"/>
                </div>
            </template>
        </div>
    </template>
    <template v-else>
        No Balance Exists
    </template>
</template>